// רכיבי בסיס משותפים.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, waitForJob, type Job } from '../api';

// ── Toasts ──
type Toast = { id: number; text: string; kind: 'ok' | 'err' };
const ToastCtx = createContext<(text: string, kind?: 'ok' | 'err') => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: 'ok' | 'err' = 'ok') => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, text, kind }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), kind === 'err' ? 8000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-wrap" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast ${t.kind === 'err' ? 'err' : ''}`}>{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ── Me / הרשאות ──
export interface Me { user: { id: string; email: string; name: string }; workspace: { id: string; name: string }; role: 'admin' | 'editor' | 'viewer'; canApproveSpecs: boolean; canExportDelivery: boolean }
export const MeCtx = createContext<Me | null>(null);
export const useMe = () => useContext(MeCtx)!;

// ── טעינת נתונים ──
export function useLoad<T>(url: string | null, deps: unknown[] = []): { data: T | null; error: string | null; loading: boolean; reload: () => void; setData: (d: T) => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!url) return;
    let alive = true;
    setLoading(true);
    api.get<T>(url).then((d) => { if (alive) { setData(d); setError(null); } }).catch((e) => { if (alive) setError(e.message); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, n, ...deps]);
  return { data, error, loading, reload: () => setN((x) => x + 1), setData };
}

export function Loading({ what = 'טוען' }: { what?: string }) { return <div className="empty"><span className="spinner" /> {what}…</div>; }
export function ErrorBox({ error }: { error: string | null }) { return error ? <div className="alert err" role="alert">{error}</div> : null; }
export function Empty({ children }: { children: ReactNode }) { return <div className="empty">{children}</div>; }

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} style={wide ? { width: 'min(980px, 100%)' } : undefined}>
        <div className="row between"><h2 style={{ margin: 0 }}>{title}</h2><button className="btn small" onClick={onClose} aria-label="סגירה">✕</button></div>
        <div style={{ marginTop: 12 }}>{children}</div>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = { queued: 'בתור', running: 'רץ', succeeded: 'הצליח', failed: 'נכשל', canceled: 'בוטל' };
export function JobBadge({ status }: { status: string }) {
  const cls = status === 'succeeded' ? 'ok' : status === 'failed' ? 'err' : status === 'canceled' ? '' : 'info';
  return <span className={`badge ${cls}`}>{STATUS_LABEL[status] ?? status}</span>;
}

/** מריץ עבודה ומציג מצב. */
export function useJob() {
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (start: () => Promise<{ jobId: string }>) => {
    setBusy(true); setJob(null);
    try {
      const { jobId } = await start();
      const j = await waitForJob(jobId, setJob);
      return j;
    } finally { setBusy(false); }
  }, []);
  return { job, busy, run, setJob };
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="field"><span>{label}</span>{children}{hint && <span className="small muted">{hint}</span>}</label>;
}

export function fmtDate(s: string | null | undefined) {
  if (!s) return '';
  return new Date(s).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
}

export function fmtKb(bytes: number | null | undefined) {
  if (bytes == null) return '';
  return `${(bytes / 1024).toFixed(1)} KB`;
}
